import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Modal from "../../components/Modal/Modal";
import Select from "../../components/Select/Select";
import Input from "../../components/Input/Input";
import DatePickerField from "../../components/FormikComponents/DatePickerField";
import styles from "./BulkEditModal.module.scss";
import Button from "../../components/Button/Button";
import { hideModal } from "../../redux/modalStore";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import { useNavigate } from "react-router-dom";
import Pill from "../../components/Pill/Pill";
import classNames from "classnames";
import {
  clearResetSelection,
  resetSelection,
} from "../../redux/rowSelectionStore";
import Swal from "sweetalert2";
import { hideOverlay, showOverlay } from "../../redux/overlayLoaderStore";
import { Tooltip } from "react-tooltip";
import IconSelect from "../../components/IconSelect/IconSelect";
import ClosedLockIcon from "../../icons/lock.svg";
import PublicIcon from "../../icons/public.svg";
import { useTranslation } from "react-i18next";
import RaciAssignment from "../../components/RaciAssignment/RaciAssignment";

const BulkEditModal = ({ modalType, orgId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sessionToken = localStorage.getItem("session_token");

  const openedModal = useSelector((state) => state.modal.openedModal);
  const user = useSelector((state) => state.user.user);

  const isConsultant = user?.Role === "Consultant";
  const isQMSAdmin = user?.Role === "QMSAdmin";
  const isBulkEditModal = openedModal?.name === "bulk_edit_modal";
  const selectedData = isBulkEditModal
    ? Array.isArray(openedModal?.data)
      ? openedModal.data
      : openedModal?.data
        ? [openedModal.data]
        : []
    : [];
  const usesRaciAssignment =
    modalType === "findings" ||
    modalType === "improvements" ||
    modalType === "audits";
  const selectedKeys = selectedData
    .map((item) => item.key)
    .filter((key) => key);
  const typeLabelMap = {
    findings: t("bulkEdit.typeLabels.findings"),
    improvements: t("bulkEdit.typeLabels.improvements"),
    audits: t("bulkEdit.typeLabels.audits"),
  };
  const titleLabel = t("bulkEdit.title", {
    count: selectedData.length,
    typeLabel: typeLabelMap[modalType],
  });
  const title = selectedKeys.length
    ? `${titleLabel}: ${selectedKeys.join(", ")}`
    : titleLabel;

  // Dynamic configuration
  const config = {
    improvements: {
      endpoint: `/nocodb/improvements?org_id=${orgId}`,
      fields: [
        "date",
        "status",
        "responsible",
        "accountable",
        "consulted",
        "informed",
        ...(isConsultant || isQMSAdmin ? ["private"] : []),
      ],
    },
    findings: {
      endpoint: `/nocodb/findings?org_id=${orgId}`,
      fields: [
        "date",
        "status",
        "responsible",
        "accountable",
        "consulted",
        "informed",
        ...(isConsultant || isQMSAdmin ? ["private"] : []),
      ],
    },
    audits: {
      endpoint: `/nocodb/audit_tasks?org_id=${orgId}`,
      fields: [
        "date",
        "status",
        "responsible",
        "accountable",
        "consulted",
        "informed",
        ...(isConsultant ? ["type"] : []),
        ...(isConsultant || isQMSAdmin ? ["private"] : []),
      ],
    },
  };

  const { endpoint } = config[modalType];

  const validationSchema = Yup.object().shape({
    date: Yup.date().nullable(),
    status: Yup.string()
      .oneOf(["", "Open", "In Progress", "In Review", "Done"])
      .nullable(),
    responsible: Yup.string().email(t("bulkEdit.validation.invalidEmail"))
      .nullable(),
    accountable: Yup.array().of(Yup.string().email(t("bulkEdit.validation.invalidEmail"))).nullable(),
    consulted: Yup.array().of(Yup.string().email(t("bulkEdit.validation.invalidEmail"))).nullable(),
    informed: Yup.array().of(Yup.string().email(t("bulkEdit.validation.invalidEmail"))).nullable(),
    ...(isConsultant &&
      modalType === "audits" && {
      type: Yup.string().oneOf(["Internal", "External"]).nullable(),
    }),
    private: Yup.boolean().nullable(),
  });

  const handleSave = async (values) => {
    dispatch(showOverlay());
    dispatch(hideModal()); // Close the modal..

    try {
      const changedFields = Object.keys(values).reduce((acc, key) => {
        const value = values[key];

        if (key === "notify") {
          return acc; // skip including it in body
        }

        // Handle date formatting
        if (key === "date" && value instanceof Date) {
          acc[key] = value.toISOString().split("T")[0];
        } else if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          if (key === "type") {
            if (value && value !== "") {
              acc[key] = value.toLowerCase();
            }
          } else {
            acc[key] = value;
          }
        }

        return acc;
      }, {});

      const payload = selectedData.map((item) => ({
        id: item.id,
        ...changedFields,
      }));

      let finalEndpoint = endpoint;
      if (modalType === "audits" && "notify" in values) {
        finalEndpoint += `&notify=${values.notify}`;
      }

      // Send the PATCH request
      await axiosInstance.patch(finalEndpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          "session-token": sessionToken,
        },
      });

      // Extract and display edited keys
      const editedKeys = selectedData.map((item) => item.key);

      // Dynamically reload data based on modalType
      const loadOptions = {
        improvements: {
          loadFindings: false,
          loadImprovements: true,
          loadAuditTasks: false,
          loadAudits: false,
          loadPermits: false,
        },
        findings: {
          loadFindings: true,
          loadImprovements: false,
          loadAuditTasks: false,
          loadAudits: false,
          loadPermits: false,
        },
        audits: {
          loadFindings: false,
          loadImprovements: false,
          loadAuditTasks: true,
          loadAudits: true,
          loadPermits: false,
        },
      };

      const {
        loadFindings,
        loadImprovements,
        loadAuditTasks,
        loadAudits,
        loadPermits,
      } = loadOptions[modalType] || {};

      // Fetch updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          loadFindings,
          loadImprovements,
          loadAuditTasks,
          loadAudits,
          loadPermits,
        ),
      );

      Swal.fire({
        toast: true,
        icon: "success",
        title: t("bulkEdit.alerts.successTitle"),
        text: t("bulkEdit.alerts.successText", {
          keys: editedKeys.join(", "),
        }),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: t("bulkEdit.alerts.errorTitle"),
        text: t("bulkEdit.alerts.errorText"),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      // Trigger row selection reset
      dispatch(resetSelection());
      setTimeout(() => dispatch(clearResetSelection()), 100); // Reset to false after a short delay

      dispatch(hideOverlay()); // Hide overlay
    }
  };

  const handleCancel = () => {
    // Trigger row selection reset
    dispatch(resetSelection());
    setTimeout(() => dispatch(clearResetSelection()), 100); // Reset to false after a short delay
    dispatch(hideModal());
  };

  const statusOptions = [
    { label: "Open", value: "Open" },
    { label: "In Progress", value: "In Progress" },
    { label: "In Review", value: "In Review" },
    { label: "Done", value: "Done" },
  ];

  return (
    <Modal
      name="bulk_edit_modal"
      title={title}
      onClose={handleCancel}
      maxWidth="40vw"
      height="70vh"
    >
      <Formik
        initialValues={{
          status: "",
          accountable: [],
          consulted: [],
          informed: [],
          ...(isConsultant && modalType === "audits" && { type: "" }),
          ...(modalType === "audits" && (isConsultant || isQMSAdmin)
            ? { private: null }
            : {}),
          ...(modalType === "audits" && { notify: true }),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({ values, dirty, isSubmitting, initialValues }) => {
          const hasRealChanges = Object.keys(values).some((key) => {
            if (key === "notify") return false; // Ignore `notify`
            return (
              JSON.stringify(values[key]) !== JSON.stringify(initialValues[key])
            );
          });

          return (
            <Form className={styles.form}>
              <div className={styles.fields}>
                <div className={styles.row}>
                  <div className={classNames(styles.field, styles.date)}>
                    <label>{t("bulkEdit.fields.date")}</label>
                    <Field name="date" component={DatePickerField} />
                    <div className={styles.helperText}>
                      {t("bulkEdit.helpers.date")}
                    </div>
                  </div>
                  <div className={classNames(styles.field, styles.status)}>
                    <label>{t("bulkEdit.fields.status")}</label>
                    <Field
                      name="status"
                      component={Select}
                      options={statusOptions}
                      text={t("bulkEdit.placeholders.status")} />
                    <div className={styles.helperText}>
                      {t("bulkEdit.helpers.status")}
                    </div>
                  </div>
                </div>
                <div className={styles.row}>
                  {isConsultant && modalType === "audits" && (
                    <div className={classNames(styles.field, styles.type)}>
                      <label>{t("bulkEdit.fields.type")}</label>
                      <Field
                        name="type"
                        component={Select}
                        options={[
                          { label: "Internal", value: "internal" },
                          { label: "External", value: "external" },
                        ]}
                        text={t("bulkEdit.placeholders.type")}
                      />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.type")}
                      </div>
                    </div>
                  )}
                  {(isConsultant || isQMSAdmin) && (
                    <div className={classNames(styles.field, styles.type)}>
                      <label>{t("bulkEdit.fields.privacy")}</label>
                      <IconSelect
                        name="private"
                        options={[
                          {
                            label: "Private",
                            value: true,
                            icon: ClosedLockIcon,
                          },
                          {
                            label: "Public",
                            value: false,
                            icon: PublicIcon,
                          },
                        ]}
                        placeholder={t(
                          "bulkEdit.placeholders.privacySelect",
                        )}
                        variant="modal"
                      />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.privacy")}
                      </div>
                    </div>
                  )}
                </div>
                {usesRaciAssignment ? (
                  <div className={styles.raciBlock}>
                    <RaciAssignment />
                    <div className={styles.helperText}>
                      {t("bulkEdit.helpers.raci")}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.field}>
                      <label>{t("bulkEdit.fields.responsible")}</label>
                      <Field
                        name="responsible"
                        component={Input}
                        placeholder={t("bulkEdit.placeholders.responsible")} />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.responsible")}
                      </div>
                    </div>
                    <div className={styles.field}>
                      <label>{t("bulkEdit.fields.accountable")}</label>
                      <Field
                        name="accountable"
                        component={Pill}
                        placeholder={t("bulkEdit.placeholders.accountable")} limit={5}
                      />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.accountable")}
                      </div>
                    </div>
                    <div className={styles.field}>
                      <label>{t("bulkEdit.fields.consulted")}</label>
                      <Field
                        name="consulted"
                        component={Pill}
                        placeholder={t(
                          "bulkEdit.placeholders.consultedEmails",
                        )} limit={5}
                      />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.consulted")}
                      </div>
                    </div>
                    <div className={styles.field}>
                      <label>{t("bulkEdit.fields.informed")}</label>
                      <Field
                        name="informed"
                        component={Pill}
                        placeholder={t(
                          "bulkEdit.placeholders.informedEmails",
                        )} limit={5}
                      />
                      <div className={styles.helperText}>
                        {t("bulkEdit.helpers.informed")}
                      </div>
                    </div>
                  </>
                )}
                {modalType === "audits" && (
                  <div className={styles.settingField}>
                    <div className={styles.settingRow}>
                      <label className={styles.labelWithInfo}>
                        <label>{t("bulkEdit.fields.notify")}</label>
                        {/*<span*/}
                        {/*  className={styles.infoIcon}*/}
                        {/*  data-tooltip-id="notify-tooltip"*/}
                        {/*  data-tooltip-content="If enabled, an email will be sent to each responsible person for the selected audit tasks."*/}
                        {/*>*/}
                        {/*  i*/}
                        {/*</span>*/}
                      </label>
                      <Tooltip
                        id="notify-tooltip"
                        place="top"
                        className={styles.tooltip}
                      />
                      <Field name="notify">
                        {({ field }) => (
                          <label className={styles.toggleSwitch}>
                            <input
                              type="checkbox"
                              {...field}
                              checked={field.value}
                            />
                            <span className={styles.slider}></span>
                          </label>
                        )}
                      </Field>
                    </div>
                    <div className={styles.helperText}>
                      {t("bulkEdit.helpers.notify")}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.actions}>
                <Button
                  text={t("bulkEdit.buttons.cancel")} onClick={handleCancel}
                  className={classNames(styles.button, styles.cancelButton)}
                />
                <Button
                  text={t("bulkEdit.buttons.save")} type="submit"
                  disabled={!hasRealChanges || isSubmitting}
                  className={classNames(styles.button, styles.saveButton)}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default BulkEditModal;
